import { useState } from 'react';
import { Box, Button, Icon, Stack, Typography } from 'ui';
import { useTheme } from '@mui/material/styles';
import {
  ButtonRow,
  InputLabelStyled,
  Item,
  StyledTooltip,
  TextFieldStyled,
  HiddenCheckbox
} from './SFIDSelector.styled';
import gql, { DEFAULT_ERROR_MESSAGE } from 'utils/gql';

import { Controller, useFormContext } from 'react-hook-form';
import { useNotification } from 'hooks';

interface ISFIDSelectorProps {
  disabled?: boolean;
  idValue?: string;
  name: string;
  currentSiteGuid: string;
}

type AccountData = {
  accountName: string;
  accountType: string;
  accountSubtype: string;
  accountStatus: string;
  accountBillingType: string;
};

interface IPopupProps extends AccountData {
  onApprove(idValue: string): void;
  onCancel(): void;
  idValue: string;
}

const fetchSalesForceAccountData = `
query salesforceAccountDetails(
     $siteGuid: String!,
     $salesforceAccountId: String!
    ) {
    salesforceAccountDetails(
        siteGuid: $siteGuid,
        salesforceAccountId: $salesforceAccountId
    ) {
    __typename
      ... on SalesforceAccountDetails {
            id
            name
            type
            subType
            billingType
            status
        }
      ... on ErrorMessage {
          message
          errorCode
      }

    }
}
`;

const ACCOUNT_NOT_FOUND_ERROR_MESSAGE = 'Account not found - Retype and Validate';
const NO_DATA = 'Add and Validate Customer SalesForce ID';

const Popup = (props: IPopupProps) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: '100%', height: '100%', padding: theme.spacing(2) }}>
      <Stack direction="column" justifyContent="flex-start" alignItems="start" spacing={0}>
        <Typography variant="body2" fontWeight={700} pl={1.5} lineHeight={theme.typography.caption.lineHeight}>
          SalesForce Account Info
        </Typography>
        <Item>
          <InputLabelStyled>Account Name:</InputLabelStyled>
          <TextFieldStyled value={props.accountName} disabled />
        </Item>

        <Item>
          <InputLabelStyled>Account Type:</InputLabelStyled>
          <TextFieldStyled value={props.accountType} disabled />
        </Item>

        <Item>
          <InputLabelStyled>Account Sub Type:</InputLabelStyled>
          <TextFieldStyled value={props.accountSubtype} disabled />
        </Item>

        <Item>
          <InputLabelStyled>Account Status:</InputLabelStyled>
          <TextFieldStyled value={props.accountStatus} disabled />
        </Item>

        <Item>
          <InputLabelStyled>Account Billing Type:</InputLabelStyled>
          <TextFieldStyled value={props.accountBillingType} disabled />
        </Item>
        <ButtonRow>
          <Button onClick={props.onCancel} color="secondary">
            <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
              Cancel
            </Typography>
          </Button>
          <Button onClick={() => props.onApprove(props.idValue)}>
            <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
              Approve
            </Typography>
          </Button>
        </ButtonRow>
      </Stack>
    </Box>
  );
};

export default function SFIDSelector({
  disabled = false,
  idValue = '',
  name,
  currentSiteGuid,
  ...other
}: ISFIDSelectorProps) {
  const [isValidated, setIsValidated] = useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [accountData, setAccountData] = useState<AccountData>({
    accountName: '',
    accountType: '',
    accountSubtype: '',
    accountStatus: '',
    accountBillingType: ''
  });
  const [idValueText, setIdValueText] = useState<string>(idValue);
  const { control, setValue, setError, clearErrors, register } = useFormContext();
  const { notifyError } = useNotification();

  const { name: sfidInputName, ref: sfidInputRef, onChange: sfidInputOnChange } = register('salesforceAccountId');

  const onValidate = async () => {
    disableInput();
    const { success } = await fetchData();
    if (success) {
      openPopup();
      clearErrors(name);
    }
    enableInput();
  };

  const disableInput = () => {
    setIsDisabled(true);
  };
  const enableInput = () => {
    setIsDisabled(false);
  };
  const openPopup = () => {
    setIsPopupOpen(true);
  };
  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const toggleIsValidated = (value: boolean) => {
    setIsValidated(value);
    setValue(name, value, { shouldValidate: true });
  };

  const approve = (idValue: string) => {
    toggleIsValidated(true);
    setValue('salesforceAccountId', idValue, { shouldValidate: false });
    closePopup();
  };
  const cancel = () => {
    toggleIsValidated(false);
    closePopup();
  };

  const fetchData = async () => {
    try {
      const {
        data: {
          salesforceAccountDetails: { errorCode, message, name, type, subType, status, billingType }
        }
      } = await gql(fetchSalesForceAccountData, {
        siteGuid: currentSiteGuid,
        salesforceAccountId: idValueText
      });

      if (name && status) {
        setAccountData({
          accountName: name,
          accountType: type,
          accountSubtype: subType,
          accountStatus: status,
          accountBillingType: billingType
        });
        return { success: true };
      } else {
        setError(name, { type: 'custom', message: message || ACCOUNT_NOT_FOUND_ERROR_MESSAGE }, { shouldFocus: true });
        return { success: false };
      }
    } catch (err: any) {
      if (err.response.errors[0].message === 'SalesforceAccountId validation failed') {
        setError(name, { type: 'custom', message: ACCOUNT_NOT_FOUND_ERROR_MESSAGE }, { shouldFocus: true });
      } else {
        notifyError(DEFAULT_ERROR_MESSAGE);
      }

      return { success: false };
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ref, ...fieldProps }, fieldState: { error } }) => {
        const props = {
          id: name,
          hiddenLabel: true,
          error: !!error,
          helperText: error?.message || ' ',
          ...fieldProps,
          ...other
        };

        return (
          <Box display="flex" flexDirection="column" alignItems="flex-start" width="100%">
            <InputLabelStyled htmlFor="SalesForceID" isDisabled={disabled}>
              <Typography variant="body2">SalesForce ID</Typography>
              <HiddenCheckbox checked={value} onChange={e => onChange(e.target.checked)} inputRef={ref} {...props} />
              {isValidated ? (
                <Icon name="checkboxCircle" color={theme => theme.palette.success.main} />
              ) : (
                <Button disabled={isPopupOpen || isDisabled} variant="text" onClick={onValidate}>
                  {isDisabled ? 'validating...' : 'validate'}
                </Button>
              )}
            </InputLabelStyled>
            <StyledTooltip
              arrow
              title={<Popup {...accountData} idValue={idValueText} onApprove={approve} onCancel={cancel} />}
              placement="left"
              open={isPopupOpen}
            >
              <TextFieldStyled
                disabled={isDisabled}
                value={idValueText}
                hasError={!!error}
                error={!!error}
                name={sfidInputName}
                ref={sfidInputRef}
                helperText={error?.message}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  closePopup();
                  setIdValueText(event.target.value);
                  toggleIsValidated(false);
                  void sfidInputOnChange(event);
                }}
              />
            </StyledTooltip>
          </Box>
        );
      }}
    />
  );
}
